import React, { useState } from "react";
import { Container, Row, Col, Card, Nav, Button } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

function PrivacyTerms({ defaultTab }) {
  const location = useLocation();
  const initialTab = defaultTab || (location.pathname.includes("terms") ? "terms" : "privacy");
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", padding: "40px 0" }}>
      <Container>
        {/* Page Header */}
        <Row className="mb-4">
          <Col lg={12} className="text-center">
            <h2 className="fw-bold mb-2" style={{ fontWeight: "700", color: "#1e293b" }}>
              OrcaX Legal & Governance
            </h2>
            <p className="text-muted mx-auto" style={{ fontWeight: "500", fontSize: "1rem", maxWidth: "600px" }}>
              Transparency and data security are core to our platform. Review our Privacy Policy and Terms of Use below.
            </p>
            <div className="mt-3">
              <Link to="/auth/login" className="btn btn-outline-primary btn-sm me-2" style={{ borderRadius: "6px" }}>
                <i className="fas fa-arrow-left me-1"></i> Back to Login
              </Link>
            </div>
          </Col>
        </Row>

        {/* Tab Navigation */}
        <Row className="justify-content-center mb-4">
          <Col lg={10}>
            <Nav variant="pills" className="justify-content-center bg-white p-2 shadow-sm rounded-pill border" style={{ maxWidth: "420px", margin: "0 auto" }}>
              <Nav.Item style={{ width: "50%", textAlign: "center" }}>
                <Nav.Link
                  active={activeTab === "privacy"}
                  onClick={() => setActiveTab("privacy")}
                  style={{
                    borderRadius: "50px",
                    fontWeight: "700",
                    cursor: "pointer",
                    backgroundColor: activeTab === "privacy" ? "#014c95" : "transparent",
                    color: activeTab === "privacy" ? "#fff" : "#64748b",
                  }}
                >
                  <i className="fas fa-user-shield me-2"></i> Privacy Policy
                </Nav.Link>
              </Nav.Item>
              <Nav.Item style={{ width: "50%", textAlign: "center" }}>
                <Nav.Link
                  active={activeTab === "terms"}
                  onClick={() => setActiveTab("terms")}
                  style={{
                    borderRadius: "50px",
                    fontWeight: "700",
                    cursor: "pointer",
                    backgroundColor: activeTab === "terms" ? "#014c95" : "transparent",
                    color: activeTab === "terms" ? "#fff" : "#64748b",
                  }}
                >
                  <i className="fas fa-file-contract me-2"></i> Terms of Use
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
        </Row>

        {/* Main Content Area */}
        <Row className="justify-content-center">
          <Col lg={10}>
            <Card className="shadow-sm border-0" style={{ borderRadius: "16px", border: "1px solid #e2e8f0" }}>
              <Card.Body className="p-4 p-md-5" style={{ color: "#334155", lineHeight: "1.7", fontSize: "0.98rem" }}>
                
                {activeTab === "privacy" ? (
                  /* ── PRIVACY POLICY CONTENT ────────────────────────────────── */
                  <div>
                    <h2 className="fw-bold mb-1" style={{ color: "#014c95", fontWeight: "700" }}>
                      Privacy Policy
                    </h2>
                    <p className="text-muted small mb-4">
                      <strong>Operator:</strong> 88th Strategies LLC (Florida LLC) | <strong>Effective Date:</strong> October 7, 2026 | <strong>Version:</strong> 2026-10-07.1
                    </p>

                    <p>
                      This Privacy Policy explains how 88th Strategies LLC, operating as OrcaX ("<strong>OrcaX</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"), collects, uses, discloses, retains, and protects personal information in connection with the OrcaX website (<code>https://orcax.tech</code>), web application (<code>https://app.orcax.tech</code>), messaging platform, accounts, and related services.
                    </p>

                    <div className="bg-light p-3 rounded border-start border-4 border-primary text-secondary mb-4">
                      <strong>Notice:</strong> This policy distinguishes between information OrcaX collects directly (account, billing, support, device data) and voter, supporter, contact, recipient, message, and campaign information uploaded or processed by customers ("<strong>Customer Data</strong>"). For Customer Data, the customer generally determines why and how information is processed, and OrcaX acts as a processor/service provider on the customer's behalf.
                    </div>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">1. Scope and Processing Roles</h4>
                    <p>
                      If you receive a political text message sent using OrcaX, the political committee, campaign, organization, or consultant identified in that message is the sender and is primarily responsible for its messaging practices. OrcaX provides messaging technology.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">2. Information We Collect</h4>
                    <p>We process information depending on your interaction:</p>
                    <ul>
                      <li><strong>Account and contact information:</strong> Names, campaign names, email addresses, phone numbers, usernames, authentication credentials.</li>
                      <li><strong>Billing and transaction information:</strong> Invoices, purchased credits, transaction history. Payment cards are processed securely by Stripe.</li>
                      <li><strong>Customer Data:</strong> Contact lists, names, phone numbers, custom fields, message bodies, media, delivery statuses, and replies.</li>
                      <li><strong>Technical and usage data:</strong> IP addresses, browser types, log data, security indicators, and diagnostics.</li>
                      <li><strong>Support correspondence:</strong> Inquiries, bug reports, and compliance communications.</li>
                    </ul>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">3. Strict Mobile Privacy Commitment</h4>
                    <div className="alert alert-success border-0 shadow-sm rounded p-3 mb-3">
                      <strong>No Sale of Mobile Data:</strong> OrcaX does not sell or rent personal information. We do not sell or share mobile telephone numbers, mobile opt-in data, consent records, or message content with third parties or affiliates for their independent marketing or promotional purposes.
                    </div>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">4. How We Use and Disclose Information</h4>
                    <p>
                      We process data to provide messaging services, authenticate accounts, maintain billing, fulfill compliance duties (with carriers, Twilio, and The Campaign Registry), prevent fraud, and comply with legal requirements. We do not use Customer Data for independent marketing or train public AI models on customer lists.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">5. Opt-Outs and STOP Requests</h4>
                    <p>
                      Recipients can opt out of any messaging campaign at any time by replying <strong>STOP</strong>, <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, <strong>QUIT</strong>, or <strong>END</strong>. The platform immediately suppresses future sends to opted-out recipients across the sender's campaigns.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">6. Data Retention and Security</h4>
                    <p>
                      We retain data only as long as reasonably needed for legitimate operational, security, and legal purposes. Contractual terms require customers to retain consent records for at least five (5) years.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">7. Your Choices and Contact</h4>
                    <p>
                      To exercise applicable privacy rights (access, correction, deletion), please submit a request via our <Link to="/contact-us">Contact Us form</Link> with "Privacy Request" in the subject, or write to: 88th Strategies LLC, 11841 SW 100th Street, Miami, FL 33186, United States.
                    </p>
                  </div>
                ) : (
                  /* ── TERMS OF USE CONTENT ────────────────────────────────── */
                  <div>
                    <h2 className="fw-bold mb-1" style={{ color: "#014c95", fontWeight: "700" }}>
                      Terms of Use
                    </h2>
                    <p className="text-muted small mb-4">
                      <strong>Operator:</strong> 88th Strategies LLC (Florida LLC) | <strong>Effective Date:</strong> October 7, 2026 | <strong>Version:</strong> 2026-10-07.4
                    </p>

                    <p>
                      These Terms of Use govern access to and use of <code>https://orcax.tech</code>, <code>https://app.orcax.tech</code>, and the accounts, messaging tools, and number services provided by 88th Strategies LLC under the OrcaX name.
                    </p>

                    <div className="alert alert-warning border-0 shadow-sm rounded p-3 mb-4">
                      <strong>IMPORTANT NOTICE:</strong> BY ACCESSING OR USING THE SERVICES, YOU AGREE TO THESE TERMS AND OUR PRIVACY POLICY. THESE TERMS GOVERN COMMERCIAL AND ORGANIZATIONAL USE, CARRIER COMPLIANCE, WARRANTY DISCLAIMERS, LIMITATIONS OF LIABILITY, AND DISPUTE RESOLUTION IN MIAMI-DADE COUNTY, FLORIDA.
                    </div>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">1. Eligibility and Organizational Use</h4>
                    <p>Account holders must be at least 18 years old. The Services are offered exclusively for lawful political, campaign, advocacy, civic, and organizational outreach in the United States.</p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">2. Compliance with Law and Messaging Rules</h4>
                    <p>
                      You must comply with all applicable laws (TCPA, FCC rules, state calling and texting statutes, election and campaign finance laws) and provider policies (Twilio Messaging Policy, Twilio AUP). Outbound traffic is classified as A2P 10DLC and requires registered brand and campaign approvals.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">3. Mandatory Recipient Consent and Record Retention</h4>
                    <p>
                      You must possess documented, affirmative opt-in consent before initiating outreach. A voter file or publicly available phone list does not constitute proof of consent. You are contractually required to retain relevant consent records for at least <strong>five (5) years</strong> after the last message sent.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">4. Sending Hours and Opt-Out Requirements</h4>
                    <p>
                      Outreach must occur strictly between <strong>8:00 a.m. and 8:00 p.m.</strong> in the recipient's local time zone. You must honor all STOP and opt-out requests immediately.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">5. Rates, Credits, and Number Rentals</h4>
                    <p>
                      Standard rates are <strong>$0.03 per outbound SMS segment</strong>, <strong>$0.08 per outbound MMS message</strong>, and <strong>$2.00 per rented phone number per month</strong>. Incoming messages and standard registration assistance are free. Services operate via prepaid token credits that do not expire.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">6. Disclaimers and Limitations of Liability</h4>
                    <p className="small text-uppercase text-muted">
                      THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." ORCAX DISCLAIMS ALL IMPLIED WARRANTIES. TOTAL AGGREGATE LIABILITY ARISING UNDER THESE TERMS SHALL NOT EXCEED THE GREATER OF $500 OR THE FEES PAID FOR SERVICES CONSUMED IN THE PRECEDING 12 MONTHS.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">7. Governing Law and Disputes</h4>
                    <p>
                      These Terms are governed by Florida law. Any disputes shall be brought exclusively in state courts in Miami-Dade County, Florida or the United States District Court for the Southern District of Florida.
                    </p>

                    <h4 className="fw-bold mt-4 mb-2 text-dark">8. Legal Notices and Contact</h4>
                    <p>
                      OrcaX is operated by 88th Strategies LLC. Notices may be submitted via our <Link to="/contact-us">Contact Us form</Link> or mailed to 88th Strategies LLC, 11841 SW 100th Street, Miami, FL 33186, United States.
                    </p>
                  </div>
                )}

              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default PrivacyTerms;
